import Logger from '../lib/logger';
import event, { type EventEmitter } from '../lib/mixins/event';
import type DiscountCodeDetector from '../lib/DiscountCodeDetector';

/** Object.assign below grafts the event mixin onto the prototype at runtime. */
interface DiscountBar extends EventEmitter {}

class DiscountBar {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	NAME: string;
	// The constructor bails before assigning these when there is no detector to bind to.
	dom!: HTMLElement;
	discountCodeTextDom!: HTMLElement | null;
	discountCodeDetector!: DiscountCodeDetector;
	discountBarActiveClass!: string;
	closeButtonDom!: HTMLElement | null;
	visibilityStateLocalStorageKey!: string;

	/**
	 * Class constructor
	 */
	constructor(dom: HTMLElement, discountCodeDetector: DiscountCodeDetector) {

		this.NAME = 'DiscountBar';

		if (!discountCodeDetector) {
			Logger.error(`${this.NAME} > discountCodeDetector is required`);
			return;
		}

		this.dom = dom;
		this.discountCodeTextDom = this.dom.querySelector<HTMLElement>(`[data-discount-code-text]`);
		this.discountCodeDetector = discountCodeDetector;
		this.discountBarActiveClass = 'discount-bar--active';
		this.closeButtonDom = this.dom.querySelector<HTMLElement>(`[data-discount-bar-close]`);
		this.visibilityStateLocalStorageKey = 'fodzyme_discount_bar_visible';
	}

	/**
	 * init - Initialize the form
	 */
	init() {
		Logger.info(`${this.NAME} > init`);

		// Check to see if there is already a discount code
		if (this.discountCodeDetector.getActiveDiscountCode()) {
			this.updateDiscountCodeText(this.discountCodeDetector.getActiveDiscountCode());
			this.showOrHide();
		}

		this.discountCodeDetector.on('discount:new', (discountCode: string | null) => {
			this.updateDiscountCodeText(discountCode);
			this.showOrHide();
		});

		this.closeButtonDom?.addEventListener('click', () => {
			this.hide();
		});
	}

	/**
	 * hide - Hide the header discount bar
	 */
	hide() {
		this.dom.classList.remove(this.discountBarActiveClass);
		this.saveVisibilityState(false);
	}

	/**
	 * saveVisibilityState - Save the visibility state to localStorage
	 */
	saveVisibilityState(state: boolean) {
		localStorage.setItem(this.visibilityStateLocalStorageKey, String(state));
	}

	/**
	 * getVisibilityState - Get the visibility state from localStorage
	 */
	getVisibilityState() {
		return localStorage.getItem(this.visibilityStateLocalStorageKey);
	}

	/**
	 * show - Show the header discount bar
	 */
	show() {
		this.dom.classList.add(this.discountBarActiveClass);
		this.saveVisibilityState(true);
	}

	/**
	 * showOrHide - Show or hide the header discount bar
	 */
	showOrHide() {
		const codeText = this.discountCodeTextDom?.textContent ?? '';
		if (codeText.length > 0 && (this.getVisibilityState() !== 'false' || this.discountCodeDetector.isDiscountCodeNew())) {
			this.show();
		} else {
			this.hide();
		}
	}

	/**
	 * updateDiscountCodeText - Update the discount code text
	 */
	updateDiscountCodeText(discountCode: string | null) {
		if (this.discountCodeTextDom) {
			this.discountCodeTextDom.textContent = discountCode;
		}
	}

}

Object.assign(DiscountBar.prototype, event);

export default DiscountBar;
